/**
 * useCreditCalculator Hook - Variable Credit Purchase System
 * Handles dynamic pricing with tiered discounts and minimum purchase requirements
 */

import { useState, useCallback, useMemo } from 'react';

// Business Rules
export const PRICING_CONFIG = {
  MINIMUM_PURCHASE: 5,
  MAXIMUM_PURCHASE: 1000,
  TIERS: [
    { min: 5, max: 25, pricePerCredit: 2.00, name: 'Standard', discount: 0 },
    { min: 26, max: 100, pricePerCredit: 1.60, name: 'Bulk', discount: 20 },
    { min: 101, max: 499, pricePerCredit: 1.40, name: 'Premium', discount: 30 },
    { min: 500, max: Infinity, pricePerCredit: 1.00, name: 'Super Premium', discount: 50 }
  ],
  QUICK_SELECT_OPTIONS: [10, 25, 50, 100, 250, 500]
};

/**
 * Custom hook for credit purchase calculations
 */
export const useCreditCalculator = () => {
  const [quantity, setQuantity] = useState(PRICING_CONFIG.MINIMUM_PURCHASE);

  // Calculate pricing details based on quantity
  const pricingDetails = useMemo(() => {
    const validQuantity = Math.max(PRICING_CONFIG.MINIMUM_PURCHASE, Math.min(quantity, PRICING_CONFIG.MAXIMUM_PURCHASE));
    
    // Find the appropriate pricing tier
    const tier = PRICING_CONFIG.TIERS.find(t => validQuantity >= t.min && validQuantity <= t.max);
    
    if (!tier) {
      throw new Error('Invalid quantity - no pricing tier found');
    }

    const pricePerCredit = tier.pricePerCredit;
    const totalPrice = validQuantity * pricePerCredit;
    const standardPrice = validQuantity * PRICING_CONFIG.TIERS[0].pricePerCredit;
    const savings = standardPrice - totalPrice;
    const savingsPercentage = tier.discount;

    return {
      quantity: validQuantity,
      pricePerCredit,
      totalPrice,
      savings: savings > 0 ? savings : 0,
      savingsPercentage,
      tier: tier.name,
      isMinimumMet: validQuantity >= PRICING_CONFIG.MINIMUM_PURCHASE,
      isMaximumExceeded: quantity > PRICING_CONFIG.MAXIMUM_PURCHASE
    };
  }, [quantity]);

  // Validation results
  const validation = useMemo(() => {
    const errors = [];
    const warnings = [];

    if (quantity < PRICING_CONFIG.MINIMUM_PURCHASE) {
      errors.push(`Minimum purchase is ${PRICING_CONFIG.MINIMUM_PURCHASE} credits (R${PRICING_CONFIG.MINIMUM_PURCHASE * PRICING_CONFIG.TIERS[0].pricePerCredit})`);
    }

    if (quantity > PRICING_CONFIG.MAXIMUM_PURCHASE) {
      errors.push(`Maximum purchase is ${PRICING_CONFIG.MAXIMUM_PURCHASE} credits`);
    }

    if (quantity >= 20 && quantity < 26) {
      warnings.push(`Purchase ${26 - quantity} more credits to get 20% bulk discount (R${((26 - quantity) * PRICING_CONFIG.TIERS[0].pricePerCredit).toFixed(2)} more)`);
    }

    if (quantity >= 90 && quantity < 101) {
      warnings.push(`Purchase ${101 - quantity} more credits to get 30% premium discount (R${((101 - quantity) * PRICING_CONFIG.TIERS[1].pricePerCredit).toFixed(2)} more)`);
    }

    if (quantity >= 450 && quantity < 500) {
      warnings.push(`Purchase ${500 - quantity} more credits to get 50% super premium discount (R${((500 - quantity) * PRICING_CONFIG.TIERS[2].pricePerCredit).toFixed(2)} more)`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      canProceed: errors.length === 0 && quantity >= PRICING_CONFIG.MINIMUM_PURCHASE
    };
  }, [quantity]);

  // Set quantity with validation
  const setValidatedQuantity = useCallback((newQuantity) => {
    const numericQuantity = parseInt(newQuantity) || PRICING_CONFIG.MINIMUM_PURCHASE;
    setQuantity(numericQuantity);
  }, []);

  // Quick select handlers
  const selectQuickAmount = useCallback((amount) => {
    setQuantity(amount);
  }, []);

  // Get next tier suggestion
  const getNextTierSuggestion = useCallback(() => {
    if (quantity < 26) {
      return {
        credits: 26,
        savings: `Save 20% (R${(quantity * 0.40).toFixed(2)} less)`,
        tier: 'Bulk'
      };
    }
    if (quantity < 101) {
      return {
        credits: 101,
        savings: `Save 30% (R${(quantity * 0.60).toFixed(2)} less)`,
        tier: 'Premium'
      };
    }
    if (quantity < 500) {
      return {
        credits: 500,
        savings: `Save 50% (R${(quantity * 1.00).toFixed(2)} less)`,
        tier: 'Super Premium'
      };
    }
    return null;
  }, [quantity]);

  return {
    // State
    quantity,
    setQuantity: setValidatedQuantity,
    
    // Pricing
    pricingDetails,
    
    // Validation
    validation,
    
    // Utilities
    selectQuickAmount,
    getNextTierSuggestion,
    
    // Configuration
    config: PRICING_CONFIG
  };
};

/**
 * Utility function to calculate pricing for any quantity (no state)
 */
export const calculatePricing = (quantity) => {
  const validQuantity = Math.max(PRICING_CONFIG.MINIMUM_PURCHASE, Math.min(quantity, PRICING_CONFIG.MAXIMUM_PURCHASE));
  const tier = PRICING_CONFIG.TIERS.find(t => validQuantity >= t.min && validQuantity <= t.max);
  
  if (!tier) {
    return null;
  }

  const pricePerCredit = tier.pricePerCredit;
  const totalPrice = validQuantity * pricePerCredit;
  const standardPrice = validQuantity * PRICING_CONFIG.TIERS[0].pricePerCredit;
  const savings = standardPrice - totalPrice;

  return {
    quantity: validQuantity,
    pricePerCredit,
    totalPrice,
    savings: savings > 0 ? savings : 0,
    savingsPercentage: tier.discount,
    tier: tier.name
  };
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount) => {
  return `R${amount.toFixed(2)}`;
};

/**
 * Format savings display
 */
export const formatSavings = (savings, percentage) => {
  if (savings <= 0) return null;
  return `Save ${formatCurrency(savings)} (${percentage}%)`;
};